import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');
  const [statusFilter, setStatusFilter] = useState<'all' | 'ongoing' | 'completed'>('all');

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const filteredComics = useMemo(() => {
    if (!slug) return [];
    let result = comics.filter((c) => c.genres.includes(slug));
    if (statusFilter === 'ongoing') {
      result = result.filter((c) => c.status === 'ongoing');
    } else if (statusFilter === 'completed') {
      result = result.filter((c) => c.status === 'completed');
    }
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'latest') {
      result = [...result].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    }
    return result;
  }, [slug, statusFilter, sortBy]);

  const [activeCategory, setActiveCategory] = useState(slug || '');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-24 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
            <h2 className="text-xl font-heading font-bold text-foreground-800">分类不存在</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm mt-4 inline-block">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        {/* Breadcrumb */}
        <div className="pt-20 pb-0">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm py-4" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <span className="text-foreground-800 font-medium">{category.name}</span>
            </nav>
          </div>
        </div>

        {/* Header */}
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-50">
                <i className={`${category.icon} text-2xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}漫画
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {filteredComics.length} 部作品
                </p>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Status Filter */}
              <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
                {(['all', 'ongoing', 'completed'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      statusFilter === status
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {status === 'all' ? '全部' : status === 'ongoing' ? '连载中' : '已完结'}
                  </button>
                ))}
              </div>

              {/* Sort */}
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-foreground-500">排序：</span>
                {([
                  { key: 'popular', label: '最热' },
                  { key: 'latest', label: '最新' },
                  { key: 'rating', label: '评分' },
                ] as const).map((sort) => (
                  <button
                    key={sort.key}
                    onClick={() => setSortBy(sort.key)}
                    className={`text-sm transition-colors whitespace-nowrap ${
                      sortBy === sort.key
                        ? 'text-primary-500 font-medium'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {sort.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Comics Grid */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5" data-product-shop>
              {filteredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-3">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - ${category.name}韩漫`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium whitespace-nowrap ${
                        comic.status === 'ongoing'
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-secondary-500 text-background-50'
                      }`}>
                        {comic.status === 'ongoing' ? '连载' : '完结'}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-white/90 text-xs">
                          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                          {comic.rating}
                        </span>
                        <span className="text-white/70 text-[10px]">{(comic.views / 10000).toFixed(0)}万人气</span>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5 truncate">{comic.author}</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">该分类下暂无漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}